import { User } from '@auth0/auth0-spa-js';
import isSSR from '@calm/web-common/dist/isSSR';
import useAppConfig from '@rhg/hooks/use-app-config';
import useAuthRedirect, {
    authRedirectQueryString,
} from '@rhg/hooks/use-auth-redirect';
import { isPostScreeningAppstate } from '@rhg/hooks/use-post-screening-signup/types';
import { useStandaloneResultsValues } from '@rhg/hooks/use-standalone-results-values';
import { logger } from '@rhg/libs/logger';
import { routes } from '@rhg/utils/routes';
import { NEW_ACCOUNT_PREFIX } from '@rhg/utils/sso';
import Router from 'next/router';
import { ReactNode, createContext, useContext, useState } from 'react';
import { AppState, Auth0Provider as OgAuth0Provider } from '..';
import { Auth0Cache } from '../auth0-cache';

type Auth0State = AppState | undefined;
export const Auth0StateContext = createContext<Auth0State>(undefined);

export function Auth0StateProvider({
    state,
    children,
}: {
    state: Auth0State;
    children: ReactNode | ReactNode[];
}) {
    return (
        <Auth0StateContext.Provider value={state}>
            {children}
        </Auth0StateContext.Provider>
    );
}

export function useAuth0State() {
    return useContext(Auth0StateContext);
}

const cache = new Auth0Cache();

interface Auth0ProviderProps {
    children: ReactNode;
}

const Auth0Provider = ({ children }: Auth0ProviderProps) => {
    const appConfig = useAppConfig();
    const { createRedirectQueryString } = useAuthRedirect();
    const { setScores, setQuestionChoices, setHasAcceptedChdpp } =
        useStandaloneResultsValues();
    const [appState, setAppState] = useState<AppState>();

    const onRedirectCallback = (appState?: AppState, user?: User) => {
        const isFirstTimeSSOUser = !!user?.sub?.includes(NEW_ACCOUNT_PREFIX);

        if (!isFirstTimeSSOUser) {
            setAppState(appState);
            if (appState && isPostScreeningAppstate(appState)) {
                setScores({
                    gad: appState.gadScore,
                    phq: appState.phqScore,
                    selfHarmThoughts: appState.selfHarmScore,
                });

                setQuestionChoices(appState.answers);
                setHasAcceptedChdpp(appState.hasAcceptedChdpp);
            }
            // appState.returnTo may be stale (e.g. '/') while the redirect param
            // in the callback URL already has the correct destination. Prefer the
            // URL's redirect param when returnTo is missing or just '/'.
            const urlRedirect = new URLSearchParams(window.location.search).get(
                authRedirectQueryString
            );
            const returnTo =
                appState?.returnTo && appState.returnTo !== '/'
                    ? appState.returnTo
                    : urlRedirect ?? '/';
            Router.replace(
                `${routes.auth}?${authRedirectQueryString}=${encodeURIComponent(
                    returnTo
                )}`
            ).catch((err) =>
                logger.error(
                    'Failed to redirect after Auth0 callback.',
                    {},
                    err as Error
                )
            );
        }
    };

    return (
        <Auth0StateProvider state={appState}>
            <OgAuth0Provider
                domain={appConfig.NEXT_PUBLIC_AUTH_DOMAIN}
                clientId={appConfig.NEXT_PUBLIC_AUTH_CLIENT_ID}
                authorizationParams={{
                    audience: appConfig.NEXT_PUBLIC_AUTH_AUDIENCE,
                    redirect_uri: isSSR()
                        ? undefined
                        : `${
                              window.location.origin
                          }/auth?${createRedirectQueryString()}`,
                    // 7 hours and 58 minutes to avoid edge case of the token expiring right at 8 hours.
                    max_age: (60 * 8 - 2) * 60,
                }}
                onRedirectCallback={onRedirectCallback}
                useRefreshTokens={true}
                useRefreshTokensFallback={true}
                // Slightly longer leeway, as we were seeing expired JWTs in our API logs
                leeway={90}
                /**
                 * Not ideal for security since this widens the XSS attack vector.
                 * Justification for why we are using this and possible further mitigations are in the below ticket:
                 * https://calmdotcom.atlassian.net/browse/EVE-3826
                 */
                cache={cache}
            >
                {children}
            </OgAuth0Provider>
        </Auth0StateProvider>
    );
};

export default Auth0Provider;
